import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { toast } from 'sonner';

import { useAgentConfig, useVoiceAgents } from '@/app/(app)/admin/agent/hooks';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';

type VoiceAgent = ApiSchemas['VoiceAgentRead'];

interface VoiceAgentContextType {
  agent: VoiceAgent;
  update: (agent: Partial<VoiceAgent>) => void;
  save: () => void;
  publish: () => void;
  syncWithConfig: () => void;
  discard: () => void;
  isSaving: boolean;
  isPublishing: boolean;
  isSyncing: boolean;
  isChanged: boolean;
  isPublishable: boolean;
  isSyncNeeded: boolean;
  isCallOpen: boolean;
  setIsCallOpen: (isOpen: boolean) => void;
}

const VoiceAgentContext = createContext<VoiceAgentContextType | null>(null);

export function useVoiceAgentCtx() {
  const ctx = useContext(VoiceAgentContext);
  if (!ctx) {
    throw new Error('useVoiceAgentCtx must be used within a VoiceAgentProvider');
  }
  return ctx;
}

interface Props {
  agent: VoiceAgent;
  children: ReactNode;
}

export function VoiceAgentProvider({ agent: initialAgent, children }: Props) {
  const { api } = useApiV1Ctx();
  const [agent, setAgent] = useState(initialAgent);
  const [isCallOpen, setIsCallOpen] = useState(false);

  const { data: config } = useAgentConfig(agent.agent_config_id);

  const isChanged = useMemo(() => {
    return !isEqual(getAgentUpdateData(agent), getAgentUpdateData(initialAgent));
  }, [agent, initialAgent]);
  const isPublishable = useMemo(() => {
    if (!agent.published_at) return true;
    return new Date(agent.updated_at) > new Date(agent.published_at);
  }, [agent.published_at, agent.updated_at]);
  const isSyncNeeded = useMemo(() => {
    if (!config) return false;
    return new Date(config.updated_at) > new Date(agent.updated_at);
  }, [config, agent.updated_at]);

  const update = useCallback((agent: Partial<VoiceAgent>) => {
    setAgent((prev) => ({ ...prev, ...agent }));
  }, []);

  const { refetch } = useVoiceAgents();
  const { mutate: saveVoiceAgent, isPending: isSaving } = api.agentVoice.updateVoiceAgent.useMutation(
    { path: { id: agent.id } },
    {
      onSuccess: (data) => {
        setAgent(data);
        refetch();
        toast.success('Voice agent updated');
      },
    }
  );

  const { mutate: publish, isPending: isPublishing } = api.agentVoice.publishVoiceAgent.useMutation(
    { path: { id: agent.id } },
    {
      onSuccess: (data) => {
        setAgent(data);
        refetch();
        toast.success('Voice agent published');
      },
    }
  );

  const { mutate: syncWithConfig, isPending: isSyncing } = api.agentVoice.syncVoiceAgent.useMutation(
    { path: { id: agent.id } },
    {
      onSuccess: (data) => {
        setAgent(data);
        refetch();
        toast.success('Voice agent synced with config');
      },
    }
  );

  const save = useCallback(() => {
    saveVoiceAgent(getAgentUpdateData(agent));
  }, [agent, saveVoiceAgent]);

  const discard = useCallback(() => {
    setAgent(initialAgent);
  }, [initialAgent]);

  return (
    <VoiceAgentContext.Provider
      value={{
        agent,
        update,
        save,
        publish,
        discard,
        syncWithConfig,
        isChanged,
        isPublishable,
        isSaving,
        isPublishing,
        isSyncing,
        isSyncNeeded,
        isCallOpen,
        setIsCallOpen,
      }}
    >
      {children}
    </VoiceAgentContext.Provider>
  );
}

function getAgentUpdateData(agent: VoiceAgent) {
  return {
    id: agent.id,
    name: agent.name,
    blocks: agent.blocks,
  };
}
